import React from 'react';
import { useGameStore } from '../store/gameStore';
import clsx from 'clsx';
import { Hexagon } from 'lucide-react';

interface HexMapProps {
  onSectorSelect: (sectorId: string) => void;
  onContextMenu: (e: React.MouseEvent, sectorId: string) => void;
}

export const HexMap: React.FC<HexMapProps> = ({ onSectorSelect, onContextMenu }) => {
  const { sectors, selectedSectorId } = useGameStore();

  // 3x3 Grid Layout Configuration
  // We'll use a staggered grid approach for the hex look
  // Row 0: S001, S002, S003
  // Row 1: S004, S005, S006 (Offset)
  // Row 2: S007, S008, S009
  
  const getPosition = (index: number) => {
    const row = Math.floor(index / 3);
    const col = index % 3;
    
    // Hexagon dimensions (approximate for visual overlap)
    const width = 220; 
    const height = 190;
    const xOffset = 110; // Half width for odd rows
    
    const x = col * width + (row % 2 === 1 ? xOffset : 0);
    const y = row * (height * 0.75); // Overlap vertically
    
    return { x, y };
  };

  if (!sectors || sectors.length === 0) {
    console.warn('HexMap: No sectors data available');
    return (
      <div className="flex items-center justify-center w-[800px] h-[600px] border-2 border-dashed border-slate-700 rounded-lg">
        <p className="text-slate-500 font-mono">NO SECTOR DATA AVAILABLE</p>
      </div>
    );
  }

  return (
    <div className="relative w-[800px] h-[600px] mx-auto border border-slate-800/30 rounded-xl bg-slate-950/20 backdrop-blur-sm">
      {sectors.slice(0, 9).map((sector, index) => {
        const { x, y } = getPosition(index);
        const isSelected = selectedSectorId === sector.id;
        const isBlue = sector.owner === 'Blue';
        const isRed = sector.owner === 'Red';

        return (
          <div
            key={sector.id}
            className={clsx(
              "absolute w-64 h-72 transition-all duration-300 cursor-pointer group flex items-center justify-center",
              "hover:z-20", // Bring to front on hover
              isSelected ? "z-10 scale-105" : "z-0"
            )}
            style={{
              left: x,
              top: y,
              clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)"
            }}
            onClick={(e) => {
              e.stopPropagation();
              onSectorSelect(sector.id);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onContextMenu(e, sector.id);
            }}
          >
            {/* Hexagon Background & Border */}
            <div className={clsx(
              "absolute inset-0 transition-colors duration-300",
              isSelected ? "bg-slate-800" : "bg-slate-900/90",
              isBlue && "bg-blue-950/80",
              isRed && "bg-red-950/80",              // MIN-06: green background tint when secured
              sector.trustScore >= 8.5 && "bg-green-950/80",              "group-hover:bg-slate-800"
            )} />
            
            {/* Border Effect (Inner) */}
            <div className={clsx(
              "absolute inset-1 clip-hex transition-colors duration-300 border-2",
              isSelected ? "border-ops-blue bg-slate-800" : "border-slate-700",
              isBlue && !isSelected && "border-blue-500/50",
              isRed && !isSelected && "border-red-500/50",              // MIN-06: bright green border for secured sectors
              sector.trustScore >= 8.5 && !isSelected && "border-green-500",              "group-hover:border-slate-500"
            )} 
            style={{
              clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)"
            }}
            />

            {/* Content */}
            <div className="relative z-10 flex flex-col items-center text-slate-300 pointer-events-none">
              <Hexagon className={clsx(
                "w-8 h-8 mb-1 transition-colors",
                sector.trustScore >= 8.5 ? "text-green-400" :
                isSelected ? "text-ops-blue" : "text-slate-600",
                isBlue && sector.trustScore < 8.5 && "text-blue-400",
                isRed && "text-red-400"
              )} />
              <span className="font-mono font-bold text-sm tracking-wider">{sector.id}</span>
              {/* MIN-07: Show sector name */}
              <span className="text-xs font-semibold text-slate-300 mt-0.5 tracking-wide">{sector.name}</span>
              <span className="text-[10px] text-slate-500 uppercase mt-0.5 font-semibold tracking-widest">
                {/* MIN-06: Show 'Secured' when trust >= 8.5 */}
                {sector.trustScore >= 8.5
                  ? 'SECURED'
                  : sector.owner === 'Neutral'
                  ? 'Contested'
                  : `${sector.owner} Sector`}
              </span>
              
              {/* Status Indicators */}
              <div className="flex gap-1 mt-2">
                <div className={clsx("w-1.5 h-1.5 rounded-full", sector.control > 50 ? "bg-green-500" : "bg-slate-700")} />
                <div className={clsx("w-1.5 h-1.5 rounded-full", sector.stability > 50 ? "bg-blue-500" : "bg-slate-700")} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
